// See tooltip-directive.js for how to use tooltips in VueJS
//
// We've included a drop-in replacement for bootstrap's data-toggle=tooltip usage
// Popper.js will determine the best location (top,right,bottom,left) of the tooltip
// For now we haven't added support for tooltip's with html content, but you can do that manually
// see https://popper.js.org/tooltip-documentation.html
//
// Usage:
//   span data-toggle="tooltip" title="This is the tooltip content" data-placement="bottom"
import type { Options } from 'tooltip.js'
import type { TooltipSizeOptions } from '@app/shared/types/tooltip'

import { default as OriginalTooltip } from 'tooltip.js'

export class Tooltip extends OriginalTooltip {
  constructor(
    element: HTMLElement,
    options?: Options & { size?: TooltipSizeOptions }
  ) {
    const innerClass = `sf-tooltip-inner--${options?.size || 'md'}`

    const textAlignClass = options?.textLeft ? 'tw-text-left' : ''

    // Move the native `title` attribute to `data-original-title` so the browser
    // does not render its default tooltip, while still making the text available
    // to the tooltip library for restoring it on dispose()
    const title = element.getAttribute('title') || ''
    element.setAttribute('data-original-title', title)
    element.removeAttribute('title')

    // Tooltip bodies default to `pointer-events: none` so the cursor is never
    // captured by the tooltip itself (prevents sticky-hover bugs). The body
    // stays clickable when html content contains a link (e.g. hover infotexts
    // rendered from liquid templates), or when the trigger explicitly opts in
    // via `data-tooltip-interactive="true"` (for non-link interactive content).
    const content =
      typeof options?.title === 'function'
        ? options.title()
        : options?.title || title
    const interactive =
      element.dataset.tooltipInteractive === 'true' ||
      (!!options?.html && /<a[\s>]/i.test(content))
    const pointerEventsStyle = interactive
      ? ''
      : 'style="pointer-events: none;"'

    super(element, {
      ...options,
      template: `<div class="sf-tooltip" role="tooltip" ${pointerEventsStyle}><div class="sf-tooltip-arrow tooltip__arrow"></div><div class="sf-tooltip-inner ${innerClass} ${textAlignClass} tooltip__inner"></div></div>`,
      container: document.body,
      boundariesElement: document.body,
    })

    // tooltip.js assigns dispose as an instance property in its constructor,
    // which shadows any prototype override. We wrap it here after super() runs.
    const originalDispose = this.dispose.bind(this)
    this.dispose = (): this => {
      // tooltip.js never clears the show timeout armed by mouseenter. If the
      // reference is unmounted before it fires (hover immediately followed by
      // a click that closes the surrounding modal), the timeout re-creates the
      // tooltip against the detached reference after disposal — leaving an
      // unhideable ghost pinned at the viewport's top-left corner.
      window.clearTimeout(this._showTimeout)
      const title = this.reference.getAttribute('data-original-title') ?? ''
      originalDispose()
      if (title) {
        this.reference.setAttribute('title', title)
        this.reference.removeAttribute('data-original-title')
      }
      return this
    }
  }
}

export default Tooltip
